import React, {Fragment, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {Dialog, DialogPanel, Transition, TransitionChild} from "@headlessui/react";
import {XMarkIcon, ChevronDownIcon, ChevronRightIcon, QuestionMarkCircleIcon} from "@heroicons/react/24/outline";
import {LoadingIcon} from "../../Common";
import * as ApiCalls from "../../ApiCalls";
import {azureTokenContext, messageBoxContext, userIdContext} from "../../../../App";
import {TaskType} from "../../../../Types/Task";
import {ToCNode} from "../../../../Types/ToCNode";
import {useLogMetric} from "../../../../hooks/useLogMetric";
import {getLeafIds, getTaskLeafIds} from "./tocTreeUtils";
import {classifySelection, applyLegacyUpdate, applyNewUpdate} from "./revisionUpdateStrategies";
import "../../../../style/PriorityTableStyles/ManuscriptModal.css";

interface ManuscriptModalProps {
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
    triggerTask: TaskType;
    allTasks: TaskType[];
    setAllTasks: React.Dispatch<React.SetStateAction<TaskType[]>>;
    setSelectedTasks: React.Dispatch<React.SetStateAction<TaskType[]>>;
    useNewFlow?: boolean;
}

export default function ManuscriptModal({
                                            isOpen,
                                            setIsOpen,
                                            triggerTask,
                                            allTasks,
                                            setAllTasks,
                                            setSelectedTasks,
                                            useNewFlow = true,
                                        }: ManuscriptModalProps) {
    const azureToken = useContext(azureTokenContext);
    const userId = useContext(userIdContext);
    const setMessageBoxProp = useContext(messageBoxContext);
    const logMetricHook = useLogMetric();

    const [checkedIds, setCheckedIds] = useState<string[]>([]);
    const [expandedTasks, setExpandedTasks] = useState<Record<string, boolean>>({});
    const [isGuideOpen, setIsGuideOpen] = useState(false);
    const [isPending, setIsPending] = useState(false);
    const [pendingAction, setPendingAction] = useState<"manus" | "date" | null>(null);

    // All tasks sharing the same DocumentId as the trigger task, trigger first
    const relatedTasks = useMemo(() => {
        if (!triggerTask) return [];
        const related = allTasks.filter(
            (t) => t.DocumentId === triggerTask.DocumentId && t.Role === triggerTask.Role
        );
        return related.sort((a, b) => {
            if (a.TaskId === triggerTask.TaskId) return -1;
            if (b.TaskId === triggerTask.TaskId) return 1;
            return 0;
        });
    }, [allTasks, triggerTask]);

    // All leaf IDs across all related tasks
    const allLeafIds = useMemo(() => {
        return relatedTasks.flatMap(getTaskLeafIds);
    }, [relatedTasks]);

    // Leaf IDs that belong to tasks with an active manuscript (not selectable)
    const disabledLeafIds = useMemo(() => {
        return relatedTasks
            .filter((t) => !!(t.WorkItemId || t.AuthorCreatedWI))
            .flatMap(getTaskLeafIds);
    }, [relatedTasks]);

    // Only the selectable (non-disabled) leaf IDs
    const selectableLeafIds = useMemo(() => {
        const disabled = new Set(disabledLeafIds);
        return allLeafIds.filter((id) => !disabled.has(id));
    }, [allLeafIds, disabledLeafIds]);

    // Build a flat list of sections (one per ToC child) with parent task reference
    const sections = useMemo(() => {
        return relatedTasks.flatMap((task) => {
            if (!task.ToC?.Children) return [];
            return task.ToC.Children.map((section) => ({
                section,
                task,
                key: `${task.TaskId}_${section.Id}`,
                leafIds: getLeafIds(section),
                isTriggerTask: task.TaskId === triggerTask?.TaskId,
                isDisabledTask: !!(task.WorkItemId || task.AuthorCreatedWI),
            }));
        });
    }, [relatedTasks, triggerTask]);

    const DISABLED_REASON = "Denne noten tilhører en oppgave som allerede har et aktivt manus";

    // Initialize state when dialog opens
    useEffect(() => {
        if (isOpen && triggerTask) {
            // Pre-check only the trigger task's leaves
            const triggerLeaves = getTaskLeafIds(triggerTask);
            setCheckedIds(triggerLeaves);

            // Expand sections from selectable tasks, collapse disabled ones
            const expanded: Record<string, boolean> = {};
            sections.forEach((s) => {
                expanded[s.key] = !s.isDisabledTask;
            });
            setExpandedTasks(expanded);

            setIsGuideOpen(false);
            setIsPending(false);
            setPendingAction(null);

            logMetric("Opened", `User opened manuscript modal for DocumentId: ${triggerTask.DocumentId}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, triggerTask?.TaskId]);

    const handleClose = () => {
        logMetric("Closed", "User closed manuscript modal");
        setIsOpen(false);
    };

    const setMessageBoxPropFunc = (msg: string) => {
        setMessageBoxProp("");
        setTimeout(() => {
            setMessageBoxProp(msg);
        }, 10);
    };

    const log = (level: string, message: string) => {
        ApiCalls.log(level, message, "ManuscriptModal", triggerTask.WorkItemId, triggerTask.Role, userId, azureToken);
    };

    const logMetric = (subCategory: string, message: string) => {
        logMetricHook({
            category: "ManuscriptModal",
            subCategory,
            message,
            workItemId: triggerTask?.WorkItemId || "",
            userRole: triggerTask?.Role || "",
        });
    };

    // Checkbox logic — only considers selectable (non-disabled) leaves
    const isAllSelected = selectableLeafIds.length > 0 && selectableLeafIds.every((id) => checkedIds.includes(id));
    const isSomeSelected = checkedIds.length > 0 && !isAllSelected;

    const handleSelectAllToggle = () => {
        if (isAllSelected) {
            setCheckedIds([]);
        } else {
            setCheckedIds([...selectableLeafIds]);
        }
        logMetric("Select all toggled", isAllSelected ? "Deselected all" : "Selected all");
    };

    const handleSectionToggle = useCallback((sectionLeafIds: string[], isDisabled: boolean) => {
        if (isDisabled) return;
        const disabledSet = new Set(disabledLeafIds);
        const selectableIds = sectionLeafIds.filter((id) => !disabledSet.has(id));
        if (selectableIds.length === 0) return;

        const allChecked = selectableIds.every((id) => checkedIds.includes(id));
        if (allChecked) {
            setCheckedIds((prev) => prev.filter((id) => !selectableIds.includes(id)));
        } else {
            setCheckedIds((prev) => {
                const newSet = new Set([...prev, ...selectableIds]);
                return Array.from(newSet);
            });
        }
    }, [checkedIds, disabledLeafIds]);

    const handleLeafToggle = useCallback((leafId: string) => {
        if (disabledLeafIds.includes(leafId)) return;
        setCheckedIds((prev) => {
            if (prev.includes(leafId)) {
                return prev.filter((id) => id !== leafId);
            }
            return [...prev, leafId];
        });
    }, [disabledLeafIds]);

    const toggleSectionExpanded = (sectionId: string) => {
        setExpandedTasks((prev) => ({...prev, [sectionId]: !prev[sectionId]}));
    };

    // Submit handlers
    const handleSubmit = (isFastRevision: boolean) => {
        const actionName = isFastRevision ? "Oppdater dato" : "Opprett manus";
        const triggerLeafIds = getTaskLeafIds(triggerTask);
        const isMainRevision = triggerLeafIds.length > 0
            && triggerLeafIds.every((id) => checkedIds.includes(id))
            && checkedIds.every((id) => triggerLeafIds.includes(id));

        setIsPending(true);
        setPendingAction(isFastRevision ? "date" : "manus");

        const data = {
            Source: triggerTask.Source,
            DocumentId: triggerTask.DocumentId,
            IsFastRevision: isFastRevision,
            IsMainRevision: isMainRevision,
            SubIds: isMainRevision ? triggerTask.PartIds : checkedIds,
            UserId: userId,
            ActorUserId: userId,
        };

        logMetric(`${actionName} clicked`, `User clicked ${actionName}, IsMainRevision: ${isMainRevision}, selected: ${checkedIds.length}/${allLeafIds.length}`);

        ApiCalls.makeRevision(data, azureToken)
            .then((response: any) => {
                if (response.Success === true) {
                    const successMsg = isFastRevision
                        ? "successMakingRevision(Fast Revision)"
                        : "successMakingRevision";
                    setMessageBoxPropFunc(successMsg);
                    applyUpdate(response.WorkItem, isFastRevision);
                    log("INFO", `${actionName} succeeded for DocumentId: ${triggerTask.DocumentId}`);
                    logMetric(`${actionName} succeeded`, `${actionName} completed for DocumentId: ${triggerTask.DocumentId}`);
                } else {
                    setMessageBoxPropFunc("generalFailMessage");
                    log("ERROR", `${actionName} failed for DocumentId: ${triggerTask.DocumentId}`);
                    logMetric(`${actionName} failed`, `${actionName} failed for DocumentId: ${triggerTask.DocumentId}`);
                }
            })
            .catch(() => {
                setMessageBoxPropFunc("generalFailMessage");
                log("ERROR", `${actionName} error for DocumentId: ${triggerTask.DocumentId}`);
                logMetric(`${actionName} failed`, `${actionName} error for DocumentId: ${triggerTask.DocumentId}`);
            })
            .finally(() => {
                setIsPending(false);
                setPendingAction(null);
                handleClose();
            });
    };

    const applyUpdate = (wi: any, isFastRevision: boolean) => {
        const classification = classifySelection(checkedIds, relatedTasks);

        let updater;
        if (!useNewFlow || classification.strategy === "legacy") {
            // Legacy: update the single affected task in-place
            const target = classification.affectedTasks[0] || triggerTask;
            updater = applyLegacyUpdate(target, wi, isFastRevision).updater;
            logMetric("Legacy update applied", `Strategy: legacy, task: ${target.TaskId}`);
        } else {
            // New: create new task, prune/remove source tasks
            updater = applyNewUpdate(triggerTask, checkedIds, classification, wi, isFastRevision).updater;
            logMetric("New update applied",
                `Strategy: new, fully removed: ${classification.fullySelectedTasks.length}, ` +
                `partially pruned: ${classification.partiallySelectedTasks.length}`);
        }

        setAllTasks(updater);
        setSelectedTasks(updater);
    };

    // Render a single ToC node and its children as checkable leaves
    const renderToCLeaves = (node: ToCNode, depth: number = 0) => {
        const isLeaf = !node.Children || node.Children.length === 0;
        if (isLeaf) {
            const isChecked = checkedIds.includes(node.Id);
            const isDisabled = disabledLeafIds.includes(node.Id);
            return (
                <label
                    key={node.Id}
                    className={`manuscript-modal-leaf-label ${isDisabled ? "disabled" : ""}`}
                    title={isDisabled ? DISABLED_REASON : undefined}
                >
                    <input
                        type="checkbox"
                        className={`manuscript-checkbox ${isChecked ? "checked" : ""} ${isDisabled ? "disabled" : ""}`}
                        checked={isChecked}
                        onChange={() => handleLeafToggle(node.Id)}
                        onClick={isDisabled ? (e) => e.preventDefault() : undefined}
                        aria-disabled={isDisabled || undefined}
                        aria-describedby={isDisabled ? "manuscript-disabled-reason" : undefined}
                    />
                    <span className={`manuscript-modal-leaf-title ${isDisabled ? "disabled" : ""}`}>{node.Title}</span>
                </label>
            );
        }
        // Non-leaf: show as a titled sub-group with its children indented below
        return (
            <div key={node.Id} className="manuscript-modal-subgroup">
                <span className="manuscript-modal-subgroup-title">{node.Title}</span>
                <div className="manuscript-modal-subgroup-children">
                    {node.Children.map((child) => renderToCLeaves(child, depth + 1))}
                </div>
            </div>
        );
    };

    const documentTitle = triggerTask?.DocumentTitle || triggerTask?.DocumentId || "";

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-10" onClose={handleClose}>
                <TransitionChild
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black bg-opacity-30"/>
                </TransitionChild>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <TransitionChild
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <DialogPanel className="w-full max-w-3xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all max-h-[85vh] flex flex-col">
                                {/* Header */}
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="text-2xl font-semibold text-gray-900">Oppdater noter</h3>
                                    <button
                                        onClick={handleClose}
                                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                                        aria-label="Lukk"
                                    >
                                        <XMarkIcon className="w-6 h-6 text-gray-900"/>
                                    </button>
                                </div>

                                {/* How it works guide */}
                                <div className={`mb-4 manuscript-modal-guide ${isGuideOpen ? "expanded" : "collapsed"}`}>
                                    <button
                                        onClick={() => {
                                            setIsGuideOpen(!isGuideOpen);
                                            logMetric("Guide toggled", isGuideOpen ? "Guide closed" : "Guide opened");
                                        }}
                                        className="manuscript-modal-guide-header"
                                    >
                                        <span className="manuscript-modal-guide-header-left">
                                            <QuestionMarkCircleIcon className="w-4 h-4 flex-shrink-0"/>
                                            <span>Slik fungerer det</span>
                                        </span>
                                        <span
                                            className="manuscript-modal-guide-close"
                                            role="img"
                                            aria-label="Lukk"
                                        >
                                            <XMarkIcon className="w-4 h-4"/>
                                        </span>
                                    </button>
                                    <div className="manuscript-modal-guide-body">
                                        <div className="manuscript-modal-guide-body-inner">
                                            <div>
                                                <p>
                                                    Her ser du alle noter under denne loven som du har oppgaver til.
                                                    Notene fra oppgaven du klikket på er allerede huket av.
                                                    Grå noter tilhører oppgaver som allerede har et aktivt manus, og kan ikke velges.
                                                </p>
                                                <p>
                                                    Du kan velge fritt blant de tilgjengelige notene.
                                                    Huk av hele seksjoner med seksjonsboksen, eller velg enkeltnoter under hver seksjon.
                                                    Den øverste boksen, ved siden av lovnavnet, velger eller fjerner alle tilgjengelige noter på én gang.
                                                </p>
                                                <p>
                                                    Hvis du bare velger noen noter fra en oppgave, blir de resterende
                                                    notene stående som en egen oppgave i listen din. Du kan komme tilbake og opprette manus for disse senere hvis du ønsker det.
                                                </p>
                                                <p>
                                                    <strong>Oppdater dato:</strong> Republiserer de valgte notene uten å opprette et manus-dokument. 
                                                    Dette vil oppdatere publiseringsdatoen for notene, og bekrefte at de er á jour.
                                                    Bruk dette alternativet hvis de valgte notene kan republiseres uten endringer.
                                                </p>
                                                <p>
                                                    <strong>Opprett manus:</strong> Oppretter et manus-dokument for de valgte
                                                    notene. Du vil få en ny oppgave i listen din for dette manus-dokumentet, med mulighet for å åpne dokumentet i nettleser eller Word.
                                                    Når du er ferdig med endringene i manuset, kan du sette det til vurdering i KText eller i forfatterportalen.
                                                </p>
                                                <p>
                                                    <strong>Ønsker du mer informasjon:</strong> Om du ønsker mer informasjon eller veiledning, finner du våre supportressurser på <a href="https://support.karnov.no" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Karnov Support</a>.
                                                    Finner du ikke det du leter etter, kan du også kontakte oss på <a href="mailto:support@karnovgroup.no" className="text-blue-600 underline">support@karnovgroup.no</a>.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Scrollable tree content */}
                                <div className="manuscript-modal-tree-container flex-1 min-h-0">
                                    {/* Accessible description for disabled items */}
                                    <span id="manuscript-disabled-reason" className="sr-only">
                                        {DISABLED_REASON}
                                    </span>
                                    {/* Select all */}
                                    <label className="manuscript-modal-select-all">
                                        <input
                                            type="checkbox"
                                            className={`manuscript-checkbox manuscript-checkbox-large ${
                                                isAllSelected ? "checked" : isSomeSelected ? "indeterminate" : ""
                                            }`}
                                            checked={isAllSelected}
                                            ref={(el) => { if (el) el.indeterminate = isSomeSelected; }}
                                            aria-checked={isSomeSelected ? "mixed" : isAllSelected}
                                            onChange={handleSelectAllToggle}
                                        />
                                        <span>{documentTitle}</span>
                                    </label>

                                    {/* Section groups — one per ToC child across all tasks */}
                                    <div className="manuscript-modal-task-group">
                                        {                                        sections.map(({section, task, key, leafIds, isTriggerTask, isDisabledTask}) => {
                                            const hasLeaves = section.Children && section.Children.length > 0;
                                            const disabledSet = new Set(disabledLeafIds);
                                            const selectableSectionLeafIds = leafIds.filter((id) => !disabledSet.has(id));
                                            const isSectionDisabled = isDisabledTask || (selectableSectionLeafIds.length === 0 && leafIds.length > 0);
                                            const allSectionChecked = selectableSectionLeafIds.length > 0 && selectableSectionLeafIds.every((id) => checkedIds.includes(id));
                                            const someSectionChecked = selectableSectionLeafIds.some((id) => checkedIds.includes(id)) && !allSectionChecked;
                                            const isExpanded = expandedTasks[key];

                                            return (
                                                <div key={key} className="mb-2">
                                                    <div className="manuscript-modal-task-header">
                                                        {hasLeaves ? (
                                                            <button
                                                                onClick={() => toggleSectionExpanded(key)}
                                                                className="manuscript-modal-chevron-btn"
                                                                aria-label={isExpanded ? "Skjul seksjoner" : "Vis seksjoner"}
                                                            >
                                                                {isExpanded
                                                                    ? <ChevronDownIcon className="w-5 h-5"/>
                                                                    : <ChevronRightIcon className="w-5 h-5"/>
                                                                }
                                                            </button>
                                                        ) : (
                                                            <div className="manuscript-modal-chevron-placeholder"/>
                                                        )}

                                                        <label
                                                            className={`manuscript-modal-task-label ${isSectionDisabled ? "disabled" : ""}`}
                                                            title={isSectionDisabled ? DISABLED_REASON : undefined}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                className={`manuscript-checkbox ${
                                                                    allSectionChecked ? "checked" : someSectionChecked ? "indeterminate" : ""
                                                                } ${isSectionDisabled ? "disabled" : ""}`}
                                                                checked={allSectionChecked}
                                                                ref={(el) => { if (el) el.indeterminate = someSectionChecked; }}
                                                                aria-checked={someSectionChecked ? "mixed" : allSectionChecked}
                                                                onChange={() => handleSectionToggle(leafIds, isDisabledTask)}
                                                                onClick={isSectionDisabled ? (e) => e.preventDefault() : undefined}
                                                                aria-disabled={isSectionDisabled || undefined}
                                                                aria-describedby={isSectionDisabled ? "manuscript-disabled-reason" : undefined}
                                                            />
                                                            <div className="manuscript-modal-task-label-text">
                                                                <span className={`manuscript-modal-task-title ${isSectionDisabled ? "disabled" : ""}`}>
                                                                    {section.Title}
                                                                    {isTriggerTask && sections.length > 1 && (
                                                                        <span className="ml-2 text-xs font-normal text-blue-600">(valgt oppgave)</span>
                                                                    )}
                                                                    {isSectionDisabled && (
                                                                        <span className="ml-2 text-xs font-normal text-gray-400">(har aktivt manus)</span>
                                                                    )}
                                                                </span>
                                                            </div>
                                                        </label>
                                                    </div>

                                                    {hasLeaves && isExpanded && (
                                                        <div className="manuscript-modal-leaves">
                                                            {section.Children.map((child) => renderToCLeaves(child))}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Footer actions */}
                                <div className="manuscript-modal-footer">
                                    <button
                                        onClick={() => handleSubmit(true)}
                                        disabled={isPending || checkedIds.length === 0}
                                        className="manuscript-modal-btn-secondary"
                                    >
                                        {isPending && pendingAction === "date" && <LoadingIcon/>}
                                        Oppdater dato
                                    </button>
                                    <button
                                        onClick={() => handleSubmit(false)}
                                        disabled={isPending || checkedIds.length === 0}
                                        className="manuscript-modal-btn-primary"
                                    >
                                        {isPending && pendingAction === "manus" && <LoadingIcon/>}
                                        Opprett manus
                                    </button>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
}